/**
 * THE PUBLIC FAQ — single source of truth for /faq, /llms.txt and the FAQPage JSON-LD.
 *
 * Written for AEO (Answer Engine Optimization): the goal is to be the passage an answer engine
 * QUOTES, not a page that ranks. That imposes three rules on everything in this file, and they
 * are the reason it reads the way it does:
 *
 *   1. EVERY ANSWER IS SELF-CONTAINED. No "as described above", no "see the previous question".
 *      A model retrieves ONE entry and quotes it with no neighbours attached; a cross-reference
 *      is dead weight at best and a wrong answer at worst.
 *   2. THE FIRST SENTENCE ANSWERS THE QUESTION. Context, caveats and mechanism come after. An
 *      engine that truncates keeps the top.
 *   3. CONCRETE NOUNS AND NUMBERS, never marketing adjectives. "0.35% of the assets in your account
 *      by default, capped at 3%" is quotable; "flexible risk management" is not.
 *
 * ⚠️ NO PRICES IN THIS FILE, DELIBERATELY. The live price is set by a platform_admin at runtime
 * and served from `plan_prices` via GET /plans/prices — the seeded figures in config/plans.ts are
 * a starting value, not a quote, and the live values have already been something else twice.
 * A hardcoded price here would be copied verbatim into an AI answer and outlive every correction.
 * Say the DURATION (which is structural) and point at the live number.
 */

/** A paragraph, or a bullet list (nested array). Kept this small on purpose: rendering a fuller
 *  markup vocabulary would mean a markdown dependency, and every consumer here (HTML, plain-text
 *  llms.txt, JSON-LD `acceptedAnswer.text`) has to be able to flatten it losslessly. */
export type Block = string | string[];

export type FaqEntry = {
  /** URL anchor. Stable — an answer engine may cite `/faq#…`, so renaming one breaks a citation. */
  id: string;
  q: string;
  a: Block[];
  category: CategoryId;
};

export type CategoryId =
  | "basics"
  | "accounts"
  | "billing"
  | "trading"
  | "risk"
  | "security"
  | "troubleshooting";

export const CATEGORIES: { id: CategoryId; title: string; blurb: string }[] = [
  {
    id: "basics",
    title: "The basics",
    blurb: "What twoke is, who it is for, and what it is not.",
  },
  {
    id: "accounts",
    title: "Exchange accounts & API keys",
    blurb:
      "Connecting Bybit, Binance, MEXC or cTrader, demo vs live, rotating and removing a key.",
  },
  {
    id: "billing",
    title: "Subscriptions & payment",
    blurb:
      "Per-master plans, paying in USDC, USDT or the network's own coin on Polygon, Ethereum or NEAR, upgrades and renewals.",
  },
  {
    id: "trading",
    title: "How a trade reaches your account",
    blurb: "Sizing, stop-loss and take-profit, and what gets copied.",
  },
  {
    id: "risk",
    title: "Risk controls",
    blurb:
      "Your own caps, the kill switch, and the limits that size, refuse or close a trade.",
  },
  {
    id: "security",
    title: "Security & privacy",
    blurb: "Two-factor, key encryption, custody, and your data.",
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    blurb: "Why a trade was skipped, and what to check first.",
  },
];

export const FAQ: FaqEntry[] = [
  // ── basics ───────────────────────────────────────────────────────────────────
  {
    id: "what-is-twoke",
    q: "What is twoke?",
    category: "basics",
    a: [
      "twoke is a non-custodial trading platform with two services on your own exchange accounts. Copy trading: you subscribe to a master — an automated trading engine with artificial intelligence — connect your own Bybit, Binance, MEXC or cTrader account, and the master's trades are mirrored into it, sized to the assets in your account and your risk settings. Spot: long-term buying of coins, tokenised shares and metals such as gold on your own Bybit, Binance or MEXC Spot account, when the levels of the method you choose are reached.",
      "Non-custodial means twoke never holds your crypto. Your crypto stays in your own exchange account the entire time, and the API key you connect is restricted to trading and reading — it cannot withdraw.",
    ],
  },
  {
    id: "what-is-copy-trading",
    q: "What is copy trading?",
    category: "basics",
    a: [
      "Copy trading automatically mirrors another trader's positions into your own account. When the trader you follow opens, adjusts or closes a position, the same action is placed on your account without you doing anything.",
      "On twoke the trader you follow is an automated master engine rather than a person, and the copy is not a one-to-one clone: every trade is re-sized to the assets in your account and re-checked against your own risk limits before it is placed. A trade the master takes risking 2% of the assets in its account is taken with your configured percentage of the assets in yours.",
    ],
  },
  {
    id: "services",
    q: "What services does twoke offer?",
    category: "basics",
    a: [
      "Four, all on your own accounts — twoke never holds your crypto:",
      [
        "Copy trading: a master copied into your own Bybit, Binance, MEXC or cTrader account, with a stop-loss on every trade and your own risk limits. Plans of 3, 6 or 12 months.",
        "Spot: long-term buying of coins, tokenised shares and metals such as gold on your own Bybit, Binance or MEXC Spot account, when the method's levels are reached. Sold per year.",
        "The Twokeio Telegram group, for subscribers: red and orange news 15 minutes before release, Spot alerts, and a bot that answers from this FAQ.",
        "Referrals: a subscriber who is approved earns a share of the first payment of the people they bring, paid on-chain to their wallet.",
      ],
      "Ask the bot for the price, or open the subscribe dialog, to see what each plan costs now.",
    ],
  },
  {
    id: "do-i-need-experience",
    q: "Do I need trading experience to use twoke?",
    category: "basics",
    a: [
      "You do not need to know how to analyse a chart, but you do need to understand what leveraged futures trading can do to an account. Copy trading does not remove risk — it automates a strategy that can and does lose crypto.",
      "Before you can subscribe or connect a key, twoke requires you to accept a risk disclosure and the Terms of Service, both recorded with a version against your account. If the disclosure text changes, you are asked to accept it again.",
      "The safest way to start is demo: every exchange key you connect carries a demo/live flag, and demo is the default. You can run a master against Bybit demo funds for as long as you like before deciding whether to point it at a live account.",
    ],
  },
  {
    id: "is-this-financial-advice",
    q: "Is twoke financial advice?",
    category: "basics",
    a: [
      "No. twoke is execution software, not an advisor. It mirrors a master engine's decisions into your account; it does not assess whether trading suits your circumstances, and past performance of any master does not indicate future results.",
      "You are responsible for your own decisions and for complying with the law where you live. Trading digital-asset derivatives carries a substantial risk of loss, and you can lose some or all of the crypto in the account you connect.",
    ],
  },
  {
    id: "what-do-i-need",
    q: "What do I need to start using twoke?",
    category: "basics",
    a: [
      "Four things:",
      [
        "An email address, to create your twoke account.",
        "A Bybit, Binance or MEXC account with an API key scoped to trade and read only — no withdrawal permission — or a cTrader account, connected by signing in at cTrader.",
        "A crypto wallet holding USDC, USDT or the network's own coin on Polygon, Ethereum or NEAR, plus a little of that network's coin for its fees, to pay for a subscription.",
        "Funds in the exchange account you connect. twoke never holds them; it places orders on your account.",
      ],
      "The order matters: you connect the exchange key first, then subscribe. A subscription is bought for one specific exchange account, so that account has to exist before it can be paid for.",
    ],
  },

  // ── accounts ─────────────────────────────────────────────────────────────────
  {
    id: "supported-exchanges",
    q: "Which exchanges does twoke support?",
    category: "accounts",
    a: [
      "Bybit, Binance and MEXC on USDT-margined perpetual futures, and cTrader for forex, indices, commodities and the shares your cTrader broker lists — each broker chooses its own list. Bybit, Binance and cTrader each support a demo account and a live account, and you choose which one you connect; MEXC has live accounts only.",
      "Bybit uses its Demo Trading environment, where balances persist indefinitely and can be topped up on request. Binance uses its Futures testnet, which the exchange resets periodically without notice — so treat Binance testnet history as temporary.",
    ],
  },
  {
    id: "api-key-permissions",
    q: "What permissions should my exchange API key have?",
    category: "accounts",
    a: [
      "Trade and read only. Do not enable withdrawal on a key you connect to twoke — the platform never needs it, and a key that cannot withdraw cannot be used to move your funds even if it were compromised.",
      "On Bybit, create the key under API Management with Contract trading and read permissions, and leave Withdraw off. On Binance, enable Reading and Futures, and leave Enable Withdrawals off. On MEXC, tick Futures › Account: View Account Details, Futures › Trade: View Order Details and Order Placing, and never Withdraw or Transfer. For a Spot account, tick the Spot trading permission instead of the Futures one.",
      "Link every key to the IP address the connect form shows. Binance deletes a trading key left without an IP restriction and refuses calls from any other address (error -2015); a Bybit key without one expires after 3 months, and a MEXC key after 90 days.",
    ],
  },
  {
    id: "connect-mexc",
    q: "How do I connect a MEXC account to twoke?",
    category: "accounts",
    a: [
      "Create an API key on mexc.com under API Management, tick only the permissions for the service you want, link the IP address twoke's connect form shows, and paste the Access Key and Secret Key into the form with Venue MEXC.",
      [
        "Spot: Spot › Account: View Account Details, Spot › Trade: View Order Details and Trade. Connect it with Environment Spot (live money).",
        "Copy trading: Futures › Account: View Account Details, Futures › Trade: View Order Details and Order Placing. Connect it with Environment Mainnet (live money). MEXC asks for identity verification (KYC) before it lets a key place futures orders.",
        "Never tick Withdraw or Transfer. Make one key per service, and copy the Secret Key at once: MEXC shows it only once.",
      ],
      "If you do not have a MEXC account yet, twoke's MEXC referral code is 2hSSM (link s.mexc.com/referral/1QD2P4zm1c); it has to be entered during sign-up, and it changes nothing about your account's security or what twoke can do on it.",
    ],
  },
  {
    id: "mexc-differences",
    q: "What is different about MEXC on twoke?",
    category: "accounts",
    a: [
      "MEXC has no demo: it gives no demo or testnet API key, so a MEXC account connects with real assets only, as Spot or Mainnet. To practise copy trading without risk, connect a Bybit or Binance demo account.",
      [
        "Spot buys at 1x only on MEXC; 3x and 5x are offered on Bybit and Binance.",
        "A MEXC Spot order can be as small as about 1 USDT on most pairs (about 5 USDT on Bybit and Binance). twoke reads each pair's minimum live from MEXC and never sends an order under it.",
        "Tokenised shares on MEXC come as xStocks ending in X (TSLAXUSDT) and Ondo tokens ending in ON (TSLAONUSDT); gold trades as PAXGUSDT.",
        "A copied futures trade on MEXC cannot be smaller than one contract. If your balance is too small for one, twoke skips the trade rather than raise your risk.",
      ],
    ],
  },
  {
    id: "how-are-keys-stored",
    q: "How does twoke store my exchange API key?",
    category: "accounts",
    a: [
      "Your API secret is encrypted with AES-256-GCM the moment it arrives and is only ever stored as ciphertext. The key that encrypts it is itself held by a cloud key-management service and unwrapped into process memory at startup, so a copy of the database or the configuration files does not decrypt anything on its own.",
      "The secret never leaves the database in readable form. Screens that show a connected account display a truncated API key only, and no part of the platform logs a secret.",
    ],
  },
  {
    id: "demo-or-live",
    q: "Can I try twoke on a demo account before risking real crypto?",
    category: "accounts",
    a: [
      "Yes, and it is the default. Every exchange key you connect carries a demo/live flag, and connecting without choosing gives you demo — except on MEXC, which has no demo and connects live only, and for a Spot account, which is always live.",
      "Demo and live accounts run side by side on the same platform — a demo follower gets exactly the same copied trades, sizing and stop-loss handling as a live one, against the exchange's paper balance. When you want to switch an account from demo to live you replace the key on that same account, which keeps your subscription, your risk settings and your history attached. That account must be flat (no open positions) at the moment you switch.",
    ],
  },
  {
    id: "one-account-one-master",
    q: "Can one exchange account follow more than one master?",
    category: "accounts",
    a: [
      "No. One exchange account can follow exactly one master, and this is enforced both when you subscribe and by the database itself.",
      "The reason is mechanical, not commercial. Exchanges net positions per symbol in one-way mode, so two masters trading the same account would merge into one position when they agree and close or flip each other when they disagree — leaving the first master's stop-loss orders attached to a position that no longer exists. To follow a second master, connect a second exchange account. A Bybit or Binance sub-account works for this and is the usual way to do it.",
    ],
  },
  {
    id: "how-many-accounts",
    q: "How many exchange accounts can I connect?",
    category: "accounts",
    a: [
      "As many as you subscribe. The only limit is on idle accounts: you can hold one connected account without an active subscription, and a second one is refused until the first is subscribed.",
      "This exists because every connected account opens a private WebSocket to the exchange, and exchanges cap how many connections can be opened from one address. Accounts that are actually paying for a subscription do not count toward the limit, so someone running three subscribed accounts can still add a fourth in order to subscribe it.",
    ],
  },
  {
    id: "disconnect-a-key",
    q: "How do I disconnect my exchange account from twoke?",
    category: "accounts",
    a: [
      "Remove the key from the Accounts section of your dashboard. Three conditions have to hold: you must have verified a fresh emailed step-up code, the account must not have an active subscription, and the account must be flat — no open positions.",
      "The flat check asks the exchange directly. If the stored key no longer authenticates at all, removal is allowed anyway: at that point twoke can neither see nor manage anything on that account, so refusing would only trap you. If the exchange is merely unreachable, removal is refused until it answers, because 'no response' is not evidence that you are flat.",
      "Removing a key stops all copying for that account immediately. It does not close positions that are already open — flatten those on the exchange first, which the flat precondition requires anyway.",
    ],
  },
  {
    id: "rotate-a-key",
    q: "Can I replace or rotate my API key without losing my subscription?",
    category: "accounts",
    a: [
      "Yes. Rotating replaces the key and secret on the same connected account, so your subscription, your per-account risk settings and any open positions stay attached to it. Use this whenever you regenerate a key on the exchange.",
      "The same rotation flow moves an account between demo and live, in which case the key must change too — a demo-scoped key cannot authenticate against the live venue. Moving between demo and live requires the account to be flat. The exchange itself cannot be changed: a Bybit, a Binance and a MEXC key are different accounts, so point a new connection at the other exchange instead.",
    ],
  },

  // ── billing ──────────────────────────────────────────────────────────────────
  {
    id: "plans",
    q: "What subscription plans does twoke offer?",
    category: "billing",
    a: [
      "Three, all per master: quarterly (3 months), semi-annual (6 months) and annual (12 months). There is deliberately no monthly plan — quarterly is the minimum commitment, because a strategy needs more than a few weeks before its results mean anything.",
      "Prices are set at runtime and shown live on the twoke pricing section and again at checkout, so the amount you are quoted is always the current one. A subscription covers one master on one exchange account; following a second master is a second subscription.",
    ],
  },
  {
    id: "payment-methods",
    q: "What payment methods does twoke accept?",
    category: "billing",
    a: [
      "Three networks — Polygon, Ethereum and NEAR — paid from your own crypto wallet in USDC, USDT, or the network's own coin (POL, ETH or NEAR) at the live rate for the same US-dollar price, as offered at checkout. The steps below describe Polygon. twoke does not accept credit or debit cards, bank transfers, PayPal or Apple Pay. You can also pay USDT person to person (P2P) through our official WhatsApp: see the next answer.",
      "Specifically it accepts native USDC — the token issued by Circle, contract 0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359 on Polygon. The older bridged token that many wallets also label simply USDC, whose real name is USDC.e, is a different contract and is rejected. For USDT it accepts Tether's Polygon contract 0xc2132D05D31c914a87C6611C10748AEb04B58e8F (some explorers now show it as USDT0). POL is the network's own coin: the contract reads the Chainlink POL/USD price, charges exactly the plan price in POL and returns any excess in the same transaction. Check which token your wallet actually holds before starting.",
      "The reason checkout is crypto only is the same reason twoke never holds your trading funds: payment goes from your wallet straight to a smart contract, so there is no merchant account, no stored card and no balance held on your behalf. It also means nothing can charge you again without your signature.",
    ],
  },
  {
    id: "pay-p2p",
    q: "Can I pay person to person (P2P)?",
    category: "billing",
    a: [
      "Yes. Write to our official WhatsApp, +1 849 875 4383 — that number only. Give your twoke email, the plan you want and which exchange account it is for. The price is the same as at checkout, paid in USDT person to person. P2P is crypto only: MonCash, NatCash, gourdes and cash are not accepted.",
      "Once the payment is received, the team activates the subscription on your account and it appears on your dashboard. As with every plan, there is no refund.",
      "You always write first. twoke never writes to you first, never asks you to pay on Telegram or on any other number, and never asks for a code, a password or a recovery phrase. Anyone who does is not twoke.",
    ],
  },
  {
    id: "what-is-spot",
    q: "What is Spot on twoke?",
    category: "basics",
    a: [
      "Spot is long-term buying, separate from copy trading. You choose coins, tokenised shares or metals such as gold and a buying method, and twoke places the buys on your own Bybit, Binance or MEXC account when the method's levels are reached. Your coins stay in your account; twoke never holds them.",
      "It needs an exchange account connected as a Spot account (an account connected for copying is not used for Spot) and an active Spot subscription, sold per year only. One Spot subscription covers one Spot account on each exchange — Bybit, Binance and MEXC; a second Spot account on the same exchange needs its own. Paying again while covered extends your time. Spot is not offered on cTrader, which has no spot market.",
      "Buying at 3x or 5x (on Bybit or Binance; MEXC Spot buys at 1x only) and the optional take-profit and stops each need a risk agreement you sign first. Your plans, your portfolio and the latest breakouts are on the Spot pages of the console.",
    ],
  },
  {
    id: "referrals",
    q: "How do referrals work?",
    category: "billing",
    a: [
      "A subscriber can apply to refer from the Referrals page: the wallet you are paid to (Polygon or Ethereum), why you want to refer, and how many people you expect to bring. twoke reviews each application.",
      "Once approved you get a referral code and link. A person who signs up through your link, or types your code before their first payment, is attached to you once and for good. Your share of their first payment is paid on-chain straight to your wallet in the same transaction — twoke never holds it.",
      "A referrer whom nobody has joined in 30 days expires and can apply again.",
    ],
  },
  {
    id: "telegram-group",
    q: "How do I join the twoke Telegram group?",
    category: "basics",
    a: [
      "The group is for subscribers: anyone with an active Spot or copy-trading subscription. Press Join the Telegram group on your dashboard or the Spot page, start the bot @twokeiobot, and it sends you a join link. A short check in the bot (tap the right emoji within 5 minutes) lets you in.",
      "The group posts red and orange economic news 15 minutes before release, Spot alerts and the master's new lessons. Members post text only; links, media, scams and insults are removed. Ask the bot a question in private, or mention it in the group, and it answers from this FAQ.",
      "When your subscription ends you are removed from the group, and you can rejoin once it is active again. twoke staff never write to you first and never ask for a recovery phrase, a code or money.",
    ],
  },
  {
    id: "how-to-pay",
    q: "How do I pay for a twoke subscription, step by step?",
    category: "billing",
    a: [
      "Paying takes one or two wallet confirmations and about a minute. From the subscribe dialog, choose the master, the plan, which of your connected exchange accounts the subscription is for, and the network — Polygon, Ethereum or NEAR — then:",
      [
        "On Polygon or Ethereum: connect your wallet. twoke asks it to switch to that network (Polygon PoS is chain ID 137, Ethereum chain ID 1) and offers to add the network if it is missing.",
        "Paying in USDC or USDT: when the token supports a signed permit, sign it for exactly the plan price — a signature, not a transaction, so it costs no gas and nothing stays approved afterwards. A token without a permit, such as USDT on Ethereum, asks instead for one approval transaction for exactly the plan price.",
        "Confirm the subscription. This one transaction transfers the stablecoin (or the POL or ETH, if you chose the network's own coin — then there is no signature step and any excess is returned) and records your subscription on-chain.",
        "On NEAR: choose your NEAR wallet in the list twoke opens — Meteor Wallet, HERE, HOT or Nightly — and confirm one transaction, in USDC, USDT or NEAR.",
      ],
      "You need a little of the network's own coin to pay the transaction fee: POL on Polygon, ETH on Ethereum, NEAR on NEAR (the permit signature costs none). Polygon gas has been paid in POL rather than MATIC since the 2024 migration, so a wallet still funded with the old token cannot pay fees.",
      "Your subscription activates when the payment service observes the confirmed transaction, not when the browser returns. A slow confirmation is therefore not a failed payment — leave the page open or come back to your dashboard shortly.",
      "Nothing renews automatically and no payment method is stored, because there is nothing to store: each payment is a signature from your own wallet. A subscription simply expires unless you renew it.",
    ],
  },
  {
    id: "which-wallets",
    q: "Which crypto wallets can I use to pay?",
    category: "billing",
    a: [
      "For Polygon and Ethereum, any browser wallet that injects a standard EIP-1193 provider into the page. MetaMask is the one twoke names in its error messages and the safest default; Rabby, Coinbase Wallet's extension, Brave Wallet and similar extensions work the same way.",
      "For NEAR, a NEAR wallet: Meteor Wallet, HERE Wallet, HOT Wallet or Nightly, chosen in the list twoke opens at checkout. MetaMask and the other Polygon or Ethereum wallets cannot pay on NEAR.",
      "Your wallet must be on the network chosen at checkout: Polygon PoS (chain ID 137), Ethereum (chain ID 1) or NEAR. On Polygon or Ethereum, if the wallet is on another network, twoke asks it to switch and offers to add the network if it is not configured — you approve that prompt in the wallet, not on the page.",
      "There is no WalletConnect QR flow and no mobile deep link at the moment, so paying from a phone means using a wallet app's built-in browser rather than a desktop pairing. A hardware wallet works if it is connected through one of those browser extensions.",
    ],
  },
  {
    id: "create-wallet",
    q: "How do I create a crypto wallet to pay?",
    category: "billing",
    a: [
      "A wallet is an app you install yourself; twoke never creates, holds or sees it. Which one depends on the network you will pay on:",
      [
        "Polygon or Ethereum: install MetaMask from metamask.io (the browser extension, or the app from your phone's app store), choose Create a new wallet and set a password.",
        "NEAR: open Meteor Wallet (meteorwallet.app) or HERE, HOT or Nightly, and create a new NEAR account.",
      ],
      "The wallet then shows a recovery phrase of 12 or 24 words. Write it on paper and keep it offline. Anyone who has it controls the wallet, and nobody can recover it for you if it is lost.",
      "Never give the recovery phrase or a private key to anyone, and never type it into a website or a chat. twoke, its team and its Telegram group will never ask for it; whoever asks is trying to steal from you.",
      "To fund it, buy USDC, USDT or the network's coin on an exchange and withdraw to your wallet address on the same network you will pay on — a withdrawal sent on another network does not arrive. Keep a little of the network's coin for the fee (POL, ETH or NEAR). At checkout, twoke asks MetaMask to add Polygon if it is missing.",
    ],
  },
  {
    id: "payment-failed",
    q: "My payment failed, was rejected, or I sent the wrong token. What now?",
    category: "billing",
    a: [
      "Nothing is lost if the transaction never confirmed — a rejected or failed transaction moves no funds, and you can simply try again. The common causes are, in rough order of frequency:",
      [
        "The wallet prompt was dismissed. twoke reports this as a rejection rather than an error; start the subscribe flow again.",
        "The wallet is on the wrong network. It must be on the network chosen at checkout — Polygon PoS is chain ID 137, Ethereum chain ID 1. Approve the switch prompt, or add the network in your wallet.",
        "Not enough POL for gas. A stablecoin balance can be sufficient while the transaction still fails, because fees are paid in POL. When paying in POL itself, the wallet needs the plan price plus a 1% buffer (returned) plus gas.",
        "The wallet holds bridged USDC.e rather than native USDC. These are different contracts and only native USDC is accepted — swap or bridge to native USDC first.",
        "Only the permit was signed. Signing the permit does not pay; the transaction that follows is what transfers the stablecoin. A signature left on its own costs nothing and moves nothing.",
      ],
      "If a transaction did confirm on-chain and your subscription has not appeared, do not pay again. Check your dashboard first — a payment that cannot be routed to an account, most often because that account already follows another master, is held for you as a pending subscription to attach rather than rejected. If it is not there either, email enfo@twoke.io with the transaction hash from the network's block explorer (Polygonscan, Etherscan or NearBlocks).",
    ],
  },
  {
    id: "refunds",
    q: "Can I get a refund?",
    category: "billing",
    a: [
      "No. Subscriptions are non-refundable — not partial, not pro-rated, and not for downtime. You confirm this at the point of payment as well as in the Terms of Service.",
      "This is why quarterly is the minimum plan rather than monthly, and why every exchange key defaults to demo: twoke would rather you spend a few weeks on paper funds before committing than ask for a refund afterwards.",
    ],
  },
  {
    id: "upgrade",
    q: "What happens to my remaining time if I upgrade to a longer plan?",
    category: "billing",
    a: [
      "It is preserved, not discarded. Upgrading sets your new expiry to whichever is later — today or your current expiry — plus the full duration of the new plan. Upgrading a quarterly plan with 5 weeks left to an annual plan gives you those 5 weeks plus 12 months.",
      "Upgrades move you to a longer plan on the same master. Downgrading to a shorter plan is not supported.",
    ],
  },
  {
    id: "renewal",
    q: "How does renewal work, and does it happen automatically?",
    category: "billing",
    a: [
      "Renewal is manual — nothing charges you automatically, because payment comes from your own wallet and twoke stores no payment method.",
      "Renewing extends your expiry by the plan's duration from whichever is later, today or your current expiry. So renewing early does not waste the time you have left, and renewing after a lapse does not backdate to when it expired.",
    ],
  },
  {
    id: "what-happens-when-it-expires",
    q: "What happens when my subscription expires?",
    category: "billing",
    a: [
      "New trades stop being copied to that account immediately. The copy allocator only fans a trade out to subscriptions that are active and unexpired, so an expired account is simply absent from the list — it is not queued and it does not catch up later.",
      "Positions already open on your account are not abandoned. The stop-loss and take-profit already live on the exchange stay in place, and twoke keeps watching an account that still has an open position even after its subscription lapses, so protection is not dropped underneath a live trade. Closing or managing that position remains available to you on the exchange at any time.",
    ],
  },
  {
    id: "paid-but-not-attached",
    q: "I paid but my subscription is not attached to an account. What now?",
    category: "billing",
    a: [
      "It is held for you as a pending subscription, and your dashboard shows it with a prompt to attach it to one of your exchange accounts. Nothing is lost.",
      "This happens when a payment lands for an account that cannot receive it — most often because that account already follows a different master. Rather than reject crypto that has already moved on-chain, twoke parks the subscription until you point it at an eligible account. A parked subscription copies nothing while it waits.",
    ],
  },

  // ── trading ──────────────────────────────────────────────────────────────────
  {
    id: "how-a-trade-reaches-me",
    q: "What happens between a master's decision and an order on my account?",
    category: "trading",
    a: [
      "Four steps, in a fixed order:",
      [
        "The master engine produces a trade idea, including a required stop-loss and take-profit. It can also decline to trade, which is a normal outcome rather than a failure.",
        "A deterministic risk engine checks the idea. A model output is never the last step before an exchange — this check always runs, and it is ordinary code, not a model.",
        "The trade is sized to your account: the assets in your account are read fresh from the exchange, and the quantity is derived from your configured risk percentage and the distance to the stop.",
        "The order is placed on your account through your own API key, with the stop-loss and take-profit attached, and tagged so you can trace the position back to the exact master decision that produced it.",
      ],
    ],
  },
  {
    id: "position-sizing",
    q: "How is my position size calculated?",
    category: "trading",
    a: [
      "By default from a percentage of the assets in your own account, never from a fixed quantity. twoke reads the assets in your account from the exchange immediately before sizing, multiplies that value by your configured risk percentage to get the amount you are risking, and divides that by the distance between the entry and the stop-loss. That gives a quantity where being stopped out costs approximately what you configured.",
      "The result is then rounded DOWN to the exchange's lot size, never up, because rounding up would silently risk more than you asked for. It is also checked against your available margin rather than just the assets in your account, so a trade can be shrunk or skipped if other positions are already using your margin.",
      "Two other modes exist if you prefer them: a mirror proportional to the assets in each account, which scales the master's quantity by the ratio of the assets in your account to those in the master's, and a fixed lot, which uses the same quantity every time. All three pass the same rounding, minimum-size and margin checks.",
    ],
  },
  {
    id: "mandatory-sl-tp",
    q: "Does every copied position have a stop-loss?",
    category: "trading",
    a: [
      "Yes — a stop-loss and a take-profit are mandatory on every copy-trading position (Spot buys have none), and this is enforced at three independent layers rather than trusted to one.",
      [
        "The master engine's decision type requires both prices, so a trade idea missing either cannot be produced at all.",
        "The database rejects any position record whose stop-loss or take-profit is missing, or is on the wrong side of the entry price for the direction.",
        "The order management system confirms the protective orders are actually live at the exchange, which is the only layer that knows what the exchange really holds.",
      ],
      "If protection cannot be placed within a short window, the position is closed at market rather than left running unprotected, and a watchdog forces the same outcome for a position that is left unprotected too long. An unprotected position is treated as worse than no position.",
    ],
  },
  {
    id: "what-actions-are-copied",
    q: "Are stop moves and partial closes copied too, or only entries?",
    category: "trading",
    a: [
      "All of them. When a master moves its stop, takes partial profit or exits, the same action is applied to the position that master opened on your account, keyed to the original decision so the two stay linked.",
      "Management actions only ever touch positions that twoke opened for you on behalf of that master. A position you opened yourself on the same exchange account is never moved, closed or re-stopped by the platform — it is yours.",
    ],
  },
  {
    id: "choose-pairs",
    q: "Can I choose which pairs are copied to my account?",
    category: "trading",
    a: [
      "Yes. By default every pair the master trades is copied. Open the pair list beside that master on your dashboard and tick only the pairs you want; the master's trades on the other pairs are then not copied to that account.",
      "The choice is per master and per exchange account. The list cannot be saved empty — a subscription that copies nothing would be paid for nothing — so to copy every pair again, choose all of them.",
    ],
  },
  {
    id: "mirror-limit-orders",
    q: "What does the Mirror switch do?",
    category: "trading",
    a: [
      "Mirror decides how you enter when the master places a limit order. It is on by default.",
      [
        "Mirror on: the same limit order rests on your account at the master's price, with its stop-loss and take-profit, and fills with the master's.",
        "Mirror off: you enter at market a few seconds after the master's order fills, and the copy is skipped if the price has moved more than your maximum copy deviation.",
      ],
      "A master's market order is copied at market either way. On Binance, a limit order is copied when it fills for now; the trade is never dropped.",
    ],
  },
  {
    id: "not-every-trade",
    q: "Will I get exactly the same trades as the master?",
    category: "trading",
    a: [
      "The same decisions, but not always the same fills, and sometimes fewer trades. Several checks can legitimately skip a trade for your account while another follower takes it.",
      [
        "The price moved too far between the master's entry and your copy. If it moved beyond your deviation limit — 0.5% by default — the copy is skipped rather than placed at a worse price, because the same stop-loss at a worse entry is more risk than you configured.",
        "The size that matched your risk percentage came out below the exchange's minimum order size. The trade is skipped rather than rounded up, since rounding up would exceed the risk you set.",
        "Your own limits stopped it — your maximum concurrent positions, your daily loss limit, or your kill switch.",
      ],
      "Each skip is recorded with its reason, so a missing trade can always be explained rather than guessed at.",
    ],
  },
  {
    id: "which-markets",
    q: "Which markets and symbols does twoke trade?",
    category: "trading",
    a: [
      "Each master trades its own list of markets, which can change: crypto perpetual futures on Bybit, Binance and MEXC, and forex, gold, oil and indices on cTrader or as contracts on the other venues. Ask the Telegram bot \"which markets\" for each master's list right now. You can also limit the pairs copied to your account.",
      "A master's list can include stocks, traded as perpetual contracts. To invest in stocks for the long term, use Spot: tokenised shares on your own Bybit, Binance or MEXC account.",
      "Trading is one-way rather than hedged, meaning one position per symbol per account. This is also why one exchange account can follow only one master.",
    ],
  },
  {
    id: "news-gate",
    q: "Does twoke trade through major economic news?",
    category: "trading",
    a: [
      "No. The masters read a published economic calendar and stand down around high-impact releases — a blackout around red-flagged events and a shorter one around medium-impact events. The window covers US releases and Bank of Japan rate decisions, which are the events that move crypto hardest.",
      "The gate fails closed: if the calendar cannot be fetched or its coverage has lapsed, the engine waits instead of trading. Being blind to the schedule is treated as a reason not to trade, not a reason to proceed.",
    ],
  },

  // ── risk ─────────────────────────────────────────────────────────────────────
  {
    id: "risk-settings",
    q: "What risk settings can I control?",
    category: "risk",
    a: [
      "Five, from the risk panel in your dashboard, applied per master or as a default across them:",
      [
        "Risk per trade — the share of the assets in your account risked if a trade hits its stop. The default is 0.35% and the maximum accepted is 3%.",
        "Maximum concurrent positions — how many copied positions can be open at once. The default is 3.",
        "Maximum daily loss — trading stops for the day once the day's result — closed trades, fees and funding, plus the floating result of open positions — reaches this loss as a share of the assets in your account when it first subscribed. The default is 3%.",
        "Maximum copy deviation — how far price may move between the master's entry and yours before the copy is skipped instead of placed. The default is 0.5%.",
        "Kill switch — an immediate stop on new copied trades for that master.",
      ],
      "Your settings determine the size of each copied trade within the platform's 3% cap, can refuse a trade or close open copies when a limit is reached and, if you enable compound, raise risk after wins. They remain your decision and your responsibility.",
    ],
  },
  {
    id: "kill-switch",
    q: "How do I stop copying immediately?",
    category: "risk",
    a: [
      "Turn on the kill switch in your dashboard's risk panel. It stops new copied trades for that master straight away and needs no confirmation code, because a control that stops trading must never wait on an email.",
      "The kill switch does not close positions that are already open, and it does not remove their stop-loss or take-profit — those stay live at the exchange. To exit an open position, close it on the exchange or wait for the master's own exit. To stop copying permanently, let the subscription lapse or remove the key from that account.",
    ],
  },
  {
    id: "can-i-lose-more-than-i-deposit",
    q: "Can I lose more than the crypto in my connected account?",
    category: "risk",
    a: [
      "twoke can only place orders on the account you connect, so it cannot reach your other funds, your wallet or your bank. What happens inside that account is governed by your exchange's own rules for margin and liquidation, not by twoke.",
      "Within that account you can lose all the crypto it holds. Every position carries a stop-loss, but a stop reduces risk rather than removing it: gaps, slippage and fast markets can all fill a stop worse than its price, and leverage magnifies both directions. Only fund a connected account with crypto you can afford to lose.",
    ],
  },
  {
    id: "daily-loss-limit",
    q: "What does the daily loss limit actually do?",
    category: "risk",
    a: [
      "It stops new copied trades for the rest of the day once the day's result — closed trades, fees and funding, plus your open trades' floating profit or loss — reaches the share you configured of the assets in your account when it first subscribed — 3% by default. The day turns when the exchanges' day does, at 00:00 UTC — 8 PM New York in summer, 7 PM in winter — for crypto, gold and oil alike.",
      "The figure is read from your exchange's own booked profit-and-loss record, not estimated by twoke, so it reflects what actually happened on your account including fees. If that figure cannot be read, copying stops rather than continuing on an unknown loss total.",
    ],
  },

  // ── security ─────────────────────────────────────────────────────────────────
  {
    id: "sign-in",
    q: "How do I sign in to twoke?",
    category: "security",
    a: [
      "With your email address and a password of at least 12 characters. For your security a session lasts one hour from sign-in. Ticking remember-me keeps it if you close and reopen the browser within that hour; leaving it unticked ends it when you close the browser.",
      "If you cannot use your password, you can request a 6-digit code by email and sign in with that instead. Forgotten passwords are reset through a link emailed to you.",
    ],
  },
  {
    id: "forgot-password",
    q: "I forgot my password. How do I reset it?",
    category: "security",
    a: [
      "On the sign-in page choose Forgot password and enter your email. Two emails arrive: a link that opens the reset page, and a 6-digit code you enter there with your new password. Both are needed — a link alone does not reset anything, so a link someone else sees is useless to them.",
      "The code works once, for 15 minutes, with five attempts. A successful reset signs you out everywhere, so anyone else using your account loses it at once. Signed in, you change your password from Settings instead.",
    ],
  },
  {
    id: "two-factor",
    q: "Does twoke have two-factor authentication?",
    category: "security",
    a: [
      "Yes, on the actions that matter. Connecting or replacing an exchange API key, and exporting your data, each require a fresh 6-digit code sent to your email at the moment you perform the action — on top of already being signed in with your password.",
      "That code is stored only as a hash, expires after 5 minutes, works once, and fails closed: if the service that verifies it is unavailable, the action is refused rather than allowed through. Connecting an exchange key is additionally blocked at the database level unless the second factor was satisfied, so the check cannot be bypassed by reaching the API directly.",
    ],
  },
  {
    id: "does-twoke-hold-my-funds",
    q: "Does twoke ever hold my funds?",
    category: "security",
    a: [
      "No. Your crypto stays in your own exchange account and your own wallet at all times. twoke places orders through a trade-only API key and is paid directly from your wallet to a smart contract — there is no deposit, no pooled account and no balance held on the platform.",
      "This is a deliberate design constraint rather than a feature that might change: the platform holds no customer funds and asks for no wallet private key, and nothing in the product ever needs one.",
    ],
  },
  {
    id: "export-my-data",
    q: "Can I export or delete my data?",
    category: "security",
    a: [
      "Yes, both from the Privacy section of your settings page. Export returns your complete record — account, subscriptions, connected accounts, trade history and the security audit trail — and requires a fresh emailed code, because silently downloading everything about an account is exactly the kind of action a victim would never notice.",
      "Deletion anonymises your account and removes your connected exchange credentials, while retaining the trade and payment records with your identity stripped from them. A complete erasure of financial records is not offered, because keeping them is a legal obligation; separating them from your identity is the honest way to satisfy both.",
    ],
  },
  {
    id: "who-can-see-my-trades",
    q: "Can the master see my account or my balance?",
    category: "security",
    a: [
      "No. A master administrator sees aggregate performance for the master they manage, not individual followers' balances, positions or personal details beyond what support requires.",
      "Administrative actions that do touch a user account — a role change, an impersonation for support, adding or rotating an exchange credential — each write a permanent entry to an append-only audit log, so who did what and when is always answerable.",
    ],
  },

  // ── troubleshooting ──────────────────────────────────────────────────────────
  {
    id: "no-trades-copied",
    q: "I connected my key but nothing is being copied. Why?",
    category: "troubleshooting",
    a: [
      "The most common cause is that the account has no active subscription. A connected key with no subscription is inert by design — the copy allocator only selects accounts with an active, unexpired subscription, so an unsubscribed account receives nothing at all. Your dashboard says so per account.",
      "If the account is subscribed and still quiet, check in this order: whether your kill switch is on, whether the master has actually traded (all three masters stand down for long stretches — no trade is a normal output), whether your daily loss limit has already been hit today, and whether your API key still authenticates and has trading permission enabled.",
    ],
  },
  {
    id: "trade-skipped",
    q: "Why was a trade skipped for my account but not for others?",
    category: "troubleshooting",
    a: [
      "Because copies are checked per account, so two followers of the same master can legitimately get different results from the same decision. Four checks skip a trade for one account and not another: price moved beyond your deviation limit before your copy could be placed; the size matching your risk percentage fell below the exchange's minimum order size; your available margin was already committed to other positions; or one of your own limits — concurrent positions, daily loss, kill switch — refused it.",
      "Every skip is recorded with its reason and attributed to the master decision it came from, so it can be looked up rather than inferred.",
    ],
  },
  {
    id: "account-too-small",
    q: "Is my account too small to copy a master?",
    category: "troubleshooting",
    a: [
      "It can be, and twoke will tell you rather than quietly compensating. If your configured risk percentage produces a quantity below the exchange's minimum order size for that symbol, the trade is skipped and the reason is logged — the size is never rounded up to meet the minimum, because that would risk more than you configured.",
      "If this happens on most trades, the account is too small for that master's stop distances at your risk setting. The honest options are to fund the account further or to follow a master whose typical stop distance suits it. Raising your risk percentage until trades squeeze through works mechanically but is the wrong reason to change it.",
    ],
  },
  {
    id: "wrong-account-subscribed",
    q: "I subscribed the wrong exchange account. Can it be moved?",
    category: "troubleshooting",
    a: [
      "A subscription is bound to one specific exchange account, so it does not move on its own — but you rarely need it to. If you want that account to point at a different exchange key, rotate the key on the account instead: the subscription, risk settings and history stay attached to the account, and only the credential behind it changes.",
      "If the subscription genuinely landed on the wrong account, contact support at enfo@twoke.io before removing anything. Removing a key is refused while an active subscription is bound to that account, precisely so that crypto already paid cannot be stranded by a deletion.",
    ],
  },
];

/** Flatten one entry to plain text — used by the JSON-LD `acceptedAnswer` and by /llms.txt.
 *  Both need the same words the page shows, so they derive from this rather than restating it. */
export const answerText = (e: FaqEntry): string =>
  e.a
    .map((b) => (Array.isArray(b) ? b.map((li) => `• ${li}`).join("\n") : b))
    .join("\n\n");
